import { Button } from "@/components/ui/Button";
import {
  emptyDeliverySchedule,
  formatCutoffDescription,
  type SupplierDeliverySchedule,
  WEEKDAY_OPTIONS,
} from "@/lib/supplierDelivery";

export interface SupplierDeliveryScheduleEditorProps {
  enabled: boolean;
  schedules: SupplierDeliverySchedule[];
  onEnabledChange: (enabled: boolean) => void;
  onSchedulesChange: (schedules: SupplierDeliverySchedule[]) => void;
  disabled?: boolean;
}

export function SupplierDeliveryScheduleEditor({
  enabled,
  schedules,
  onEnabledChange,
  onSchedulesChange,
  disabled = false,
}: SupplierDeliveryScheduleEditorProps) {
  function updateSchedule(index: number, patch: Partial<SupplierDeliverySchedule>) {
    onSchedulesChange(
      schedules.map((row, i) => (i === index ? { ...row, ...patch } : row)),
    );
  }

  return (
    <div className="space-y-3 rounded-lg border border-separator bg-fill-tertiary p-4">
      <label className="flex items-center gap-2 text-sm font-medium">
        <input
          type="checkbox"
          checked={enabled}
          disabled={disabled}
          onChange={(e) => onEnabledChange(e.target.checked)}
        />
        Bestellungen bei diesem Lieferanten
      </label>

      {enabled ? (
        <div className="space-y-3">
          <p className="text-xs text-label-secondary">
            Legen Sie fest, an welchen Wochentagen geliefert wird und bis wann die Bestellung
            eingegangen sein muss. Offene Bestellungen werden zum Bestellschluss automatisch per
            E-Mail versendet, sofern sie nicht vorher manuell ausgelöst wurden.
          </p>

          {schedules.length === 0 ? (
            <p className="text-sm text-label-secondary">Noch keine Liefertage hinterlegt.</p>
          ) : null}

          {schedules.map((schedule, index) => (
            <div
              key={`${schedule.weekday}-${index}`}
              className="grid gap-3 rounded-lg border border-separator bg-background p-3 sm:grid-cols-[1fr_1fr_1fr_auto]"
            >
              <label className="flex flex-col gap-1 text-sm">
                Liefertag
                <select
                  className="eh-input"
                  disabled={disabled}
                  value={schedule.weekday}
                  onChange={(e) =>
                    updateSchedule(index, { weekday: Number(e.target.value) })
                  }
                >
                  {WEEKDAY_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="flex flex-col gap-1 text-sm">
                Bestellschluss
                <select
                  className="eh-input"
                  disabled={disabled}
                  value={schedule.cutoff_days_before}
                  onChange={(e) =>
                    updateSchedule(index, { cutoff_days_before: Number(e.target.value) })
                  }
                >
                  <option value={0}>Am Liefertag</option>
                  <option value={1}>Vortag</option>
                  <option value={2}>2 Tage vorher</option>
                  <option value={3}>3 Tage vorher</option>
                </select>
              </label>
              <label className="flex flex-col gap-1 text-sm">
                Uhrzeit
                <input
                  type="time"
                  className="eh-input"
                  disabled={disabled}
                  value={schedule.cutoff_time.slice(0, 5)}
                  onChange={(e) => updateSchedule(index, { cutoff_time: e.target.value })}
                />
              </label>
              <div className="flex items-end">
                <Button
                  type="button"
                  variant="secondary"
                  disabled={disabled}
                  onClick={() =>
                    onSchedulesChange(schedules.filter((_, i) => i !== index))
                  }
                >
                  Entfernen
                </Button>
              </div>
              <p className="text-xs text-label-secondary sm:col-span-4">
                {formatCutoffDescription(schedule.cutoff_days_before, schedule.cutoff_time)}
              </p>
            </div>
          ))}

          <Button
            type="button"
            variant="secondary"
            disabled={disabled}
            onClick={() => onSchedulesChange([...schedules, emptyDeliverySchedule()])}
          >
            Liefertag hinzufügen
          </Button>
        </div>
      ) : null}
    </div>
  );
}
